import { useEffect, useState, type FormEvent } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";

const navItems = [
  { to: "/", label: "首页", end: true },
  { to: "/archive", label: "事件档案", end: false },
  { to: "/about", label: "关于我们", end: false },
];

interface SiteHeaderProps {
  overlay?: boolean;
}

export default function SiteHeader({ overlay = false }: SiteHeaderProps) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [term, setTerm] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  const transparent = overlay && !scrolled && !menuOpen;

  const panelBase = transparent
    ? "bg-transparent text-background-50"
    : "bg-background-50/95 text-foreground-950 backdrop-blur border-b border-background-200";
  const linkBase = transparent
    ? "text-background-100 hover:text-accent-300"
    : "text-foreground-700 hover:text-primary-600";
  const activeBase = transparent ? "text-accent-300" : "text-primary-600";

  const submitSearch = (e: FormEvent) => {
    e.preventDefault();
    const q = term.trim();
    navigate(q ? `/archive?q=${encodeURIComponent(q)}` : "/archive");
    setSearchOpen(false);
    setTerm("");
  };

  return (
    <header
      className={`${overlay ? "fixed" : "sticky"} top-0 left-0 right-0 z-50 transition-colors duration-300 ${panelBase}`}
    >
      <div className="container-x flex h-16 items-center justify-between gap-4 md:h-[72px]">
        <Link to="/" className="flex items-center gap-2 shrink-0">
          <span
            className={`flex h-9 w-9 items-center justify-center rounded-md text-lg ${
              transparent ? "bg-primary-500 text-background-50" : "bg-primary-500 text-background-50"
            }`}
          >
            <i className="ri-mic-2-line"></i>
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-lg font-bold tracking-tight">独家吃瓜</span>
            <span
              className={`mt-0.5 hidden text-[10px] tracking-[0.18em] sm:block ${
                transparent ? "text-background-200" : "text-foreground-500"
              }`}
            >
              GUADEEP·REPLAY
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-7 md:flex" aria-label="主导航">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `relative whitespace-nowrap text-sm font-medium transition-colors ${linkBase} ${
                  isActive ? activeBase : ""
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            type="button"
            aria-label="打开搜索"
            onClick={() => setSearchOpen((v) => !v)}
            className={`flex h-9 w-9 cursor-pointer items-center justify-center rounded-md transition-colors ${
              transparent
                ? "text-background-50 hover:bg-background-50/15"
                : "text-foreground-700 hover:bg-background-100"
            }`}
          >
            <i className="ri-search-line text-lg"></i>
          </button>
          <Link
            to="/archive"
            className="hidden whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 md:inline-flex"
          >
            看今日复盘
          </Link>
          <button
            type="button"
            aria-label="打开菜单"
            onClick={() => setMenuOpen((v) => !v)}
            className={`flex h-9 w-9 cursor-pointer items-center justify-center rounded-md transition-colors md:hidden ${
              transparent
                ? "text-background-50 hover:bg-background-50/15"
                : "text-foreground-700 hover:bg-background-100"
            }`}
          >
            <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-3-line text-xl"}></i>
          </button>
        </div>
      </div>

      {searchOpen && (
        <div
          className={`border-t ${
            transparent ? "border-background-50/20 bg-foreground-950/70" : "border-background-200 bg-background-50"
          }`}
        >
          <form onSubmit={submitSearch} className="container-x flex items-center gap-3 py-3" data-readdy-form="search">
            <i className={`ri-search-line text-base ${transparent ? "text-background-200" : "text-foreground-500"}`}></i>
            <input
              autoFocus
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder="搜索事件、关键词，例如：换角、票务、剪辑"
              className={`h-10 w-full bg-transparent text-sm outline-none ${
                transparent ? "text-background-50 placeholder:text-background-300" : "text-foreground-950 placeholder:text-foreground-400"
              }`}
            />
            <button
              type="submit"
              className="whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              搜索
            </button>
          </form>
        </div>
      )}

      {menuOpen && (
        <div className="border-t border-background-200 bg-background-50 md:hidden">
          <nav className="container-x flex flex-col py-2" aria-label="移动端导航">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `border-b border-background-100 py-3 text-sm font-medium last:border-none ${
                    isActive ? "text-primary-600" : "text-foreground-700"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Link to="/archive" className="mt-3 mb-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-center text-sm font-medium text-background-50">
              看今日复盘
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}